/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Row, Col, Card, Spin, Select, Button, Input, Slider, Tooltip,
         DatePicker, Divider, Tag } from 'antd';
import {
    CarOutlined, ToolOutlined, WarningOutlined, UserOutlined,
    AlertOutlined, DeleteOutlined, FilterOutlined, DownloadOutlined,
    ReloadOutlined, CompressOutlined
} from '@ant-design/icons';
import {
    BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
    XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, Legend,
    ResponsiveContainer, LabelList, RadarChart, Radar, PolarGrid,
    PolarAngleAxis, PolarRadiusAxis
} from 'recharts';
import html2canvas from 'html2canvas';
import * as XLSX from 'xlsx';
import FleetService from '../../services/FleetService';
import ProgrammeSelect, { useProgrammes } from './ProgrammeSelect';
import { fleetPerms } from '../../utils/fleetPermissions';
import moment from 'moment';

const { Option } = Select;

// ─── Colours ─────────────────────────────────────────────────────────────────
const PIE_COLORS = ['#1890ff','#52c41a','#faad14','#f5222d','#722ed1','#13c2c2',
                    '#fa8c16','#eb2f96','#2f54eb','#389e0d','#d4380d','#08979c'];
const YEAR_COLORS = ['#1890ff','#52c41a','#faad14','#f5222d','#722ed1','#13c2c2'];

const fmt   = (n) => Number(n || 0).toLocaleString();
const fmtE  = (n) => `E ${Number(n || 0).toLocaleString()}`;
const toNum = (arr, key = 'cnt') => (arr || []).map(r => ({ ...r, [key]: Number(r[key] || 0) }));
const normPie = (arr, key = 'cnt') => (arr || [])
    .map(r => ({ ...r, [key]: Number(r[key] || 0) })).filter(r => r[key] > 0);

// ─── Sub-components ───────────────────────────────────────────────────────────
const KpiCard = ({ title, value, icon, color, sub }) => (
    <Card size="small" style={{ borderTop: `3px solid ${color}`, height: '100%' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ fontSize: 24, color }}>{icon}</div>
            <div style={{ flex: 1 }}>
                <div style={{ fontSize: 11, color: '#888', marginBottom: 2 }}>{title}</div>
                <div style={{ fontSize: 20, fontWeight: 700, lineHeight: 1 }}>{value}</div>
                {sub && <div style={{ fontSize: 10, color: '#aaa', marginTop: 2 }}>{sub}</div>}
            </div>
        </div>
    </Card>
);

// ─── SHARED: Period Filter Component ────────────────────────────────────────
// Drop this into any file that needs time period filtering
// Usage: <PeriodFilter value={period} onChange={setPeriod} />
// period = { preset: 'this_month'|'last_3m'|'this_year'|'last_year'|'custom'|'all', from: 'YYYY-MM-DD', to: 'YYYY-MM-DD' }
// Filter data: filterByPeriod(data, period, dateField)


const { RangePicker } = DatePicker;

const PRESETS = [
    { key: 'this_month', label: 'This Month',   from: () => moment().startOf('month'),   to: () => moment() },
    { key: 'last_3m',    label: 'Last 3 Months',from: () => moment().subtract(3,'months'),to:() => moment() },
    { key: 'this_year',  label: 'This Year',     from: () => moment().startOf('year'),    to: () => moment() },
    { key: 'last_year',  label: 'Last Year',     from: () => moment().subtract(1,'year').startOf('year'), to: () => moment().subtract(1,'year').endOf('year') },
    { key: 'all',        label: 'All Time',      from: () => null, to: () => null },
];

const DEFAULT_PERIOD = { preset: 'this_year', from: moment().startOf('year').format('YYYY-MM-DD'), to: moment().format('YYYY-MM-DD') };

function PeriodFilter({ value, onChange, style }) {
    const cur = value || DEFAULT_PERIOD;
    const handlePreset = (preset) => {
        const p = PRESETS.find(x => x.key === preset);
        onChange({
            preset,
            from: p.from() ? p.from().format('YYYY-MM-DD') : '',
            to:   p.to()   ? p.to().format('YYYY-MM-DD')   : '',
        });
    };
    const handleRange = ([s, e]) => {
        onChange({
            preset: 'custom',
            from: s ? s.format('YYYY-MM-DD') : '',
            to:   e ? e.format('YYYY-MM-DD') : '',
        });
    };
    return (
        <div style={{ display:'flex', alignItems:'center', gap:6, flexWrap:'wrap', ...style }}>
            <span style={{ fontSize:11, color:'#888', whiteSpace:'nowrap' }}>Period:</span>
            {PRESETS.map(p => (
                <button key={p.key} onClick={() => handlePreset(p.key)} style={{
                    padding:'3px 10px', border:'1px solid', borderRadius:12, cursor:'pointer',
                    fontSize:11, fontWeight: cur.preset===p.key ? 600 : 400,
                    background: cur.preset===p.key ? '#1a3a5c' : '#fff',
                    borderColor: cur.preset===p.key ? '#1a3a5c' : '#d9d9d9',
                    color: cur.preset===p.key ? '#fff' : '#595959',
                }}>{p.label}</button>
            ))}
            <RangePicker size="small" style={{ width:210 }}
                value={cur.preset==='custom' && cur.from && cur.to
                    ? [moment(cur.from), moment(cur.to)] : null}
                onChange={handleRange}
                format="DD MMM YYYY"
                placeholder={['Custom from','Custom to']}
            />
            {cur.from && cur.to && (
                <span style={{ fontSize:10, color:'#1a5b9a', background:'#e6f4ff',
                    padding:'2px 8px', borderRadius:8, whiteSpace:'nowrap' }}>
                    {moment(cur.from).format('DD MMM YYYY')} → {moment(cur.to).format('DD MMM YYYY')}
                </span>
            )}
        </div>
    );
}

function filterByPeriod(data, period, dateField) {
    if (!period || period.preset === 'all' || (!period.from && !period.to)) return data;
    return data.filter(r => {
        const d = r[dateField];
        if (!d || d === '0000-00-00') return false;
        const m = moment(d);
        if (period.from && m.isBefore(moment(period.from), 'day')) return false;
        if (period.to   && m.isAfter(moment(period.to),   'day')) return false;
        return true;
    });
}

const SectionTitle = ({ children, color = '#1a3a5c' }) => (
    <div style={{
        fontWeight: 700, fontSize: 13, color,
        margin: '20px 0 10px', paddingBottom: 6,
        borderBottom: `2px solid ${color}33`,
    }}>{children}</div>
);

const ChartCard = ({ title, id, height = 280, onExport, children }) => (
    <Card size="small" id={id}
        title={<span style={{ fontSize: 12, fontWeight: 600 }}>{title}</span>}
        extra={onExport && (
            <Tooltip title="Export chart as PNG">
                <Button size="small" icon={<DownloadOutlined />} onClick={() => onExport(id, title)} />
            </Tooltip>
        )}
        style={{ height: '100%' }}>
        <div style={{ height }}>{children}</div>
    </Card>
);

const PieLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, name }) => {
    if (percent < 0.04) return null;
    const R = Math.PI / 180;
    const r = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + r * Math.cos(-midAngle * R);
    const y = cy + r * Math.sin(-midAngle * R);
    return (
        <text x={x} y={y} fill="white" textAnchor="middle" dominantBaseline="central"
            style={{ fontSize: 10, fontWeight: 600 }}>
            {`${(percent * 100).toFixed(0)}%`}
        </text>
    );
};

// ─── Filter Bar ───────────────────────────────────────────────────────────────
function FilterBar({ options, filters, onChange, onReset, onExportAll, loading, isRegionalLocked }) {
    const [odomRange, setOdomRange] = useState([0, 500000]);
    const years = options.years || [];
    const minYear = years.length ? Math.min(...years) : 2010;
    const maxYear = years.length ? Math.max(...years) : new Date().getFullYear();

    const set = (k, v) => onChange({ ...filters, [k]: v });

    return (
        <div style={{
            background: '#f8fafc', border: '1px solid #e2e8f0',
            borderRadius: 8, padding: '14px 16px', marginBottom: 20,
        }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <div style={{ fontWeight: 600, fontSize: 13, color: '#1a3a5c', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <FilterOutlined /> Dashboard Filters
                    {Object.values(filters).some(v => v !== '' && v !== 0) && (
                        <Tag color="blue" style={{ fontSize: 10 }}>Active</Tag>
                    )}
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                    <Button size="small" icon={<ReloadOutlined />} onClick={onReset}>Reset</Button>
                    <Button size="small" icon={<DownloadOutlined />} onClick={onExportAll}
                        style={{ background: '#217346', color: '#fff', borderColor: '#217346' }}>
                        Export All (Excel)
                    </Button>
                    <Button size="small" icon={<DownloadOutlined />}
                        onClick={() => onExportAll('png')}
                        style={{ background: '#1a5b9a', color: '#fff', borderColor: '#1a5b9a' }}>
                        Export Dashboard (PNG)
                    </Button>
                </div>
            </div>

            <Row gutter={[10, 10]}>
                <Col span={4}>
                    <div style={{ fontSize: 11, color: '#666', marginBottom: 3 }}>Region</div>
                    {isRegionalLocked ? (
                        <div style={{ display:'flex', alignItems:'center', gap:6, padding:'4px 10px',
                            background:'#f9f0ff', border:'1px solid #d3adf7', borderRadius:6,
                            fontSize:12, color:'#531dab', fontWeight:500 }}>
                            🗺 {filters.region} Region only
                        </div>
                    ) : (
                        <Select value={filters.region || undefined}
                        onChange={v => set('region', v || '')}
                        placeholder="All Regions" allowClear style={{ width: '100%' }} size="small">
                        {(options.regions || []).filter(Boolean).map(r => (
                            <Option key={r} value={r}>{r}</Option>
                        ))}
                    </Select>
                    )}
                </Col>
                <Col span={5}>
                    <div style={{ fontSize: 11, color: '#666', marginBottom: 3 }}>Facility</div>
                    <Select value={filters.facility_id || undefined} onChange={v => set('facility_id', v || 0)}
                        placeholder="All Facilities" allowClear showSearch optionFilterProp="children"
                        style={{ width: '100%' }} size="small">
                        {(options.facilities || []).map(f => (
                            <Option key={f.id} value={f.id}>{f.name}</Option>
                        ))}
                    </Select>
                </Col>
                <Col span={3}>
                    <div style={{ fontSize: 11, color: '#666', marginBottom: 3 }}>Fuel Type</div>
                    <Select value={filters.fuel_type || undefined} onChange={v => set('fuel_type', v || '')}
                        placeholder="All" allowClear style={{ width: '100%' }} size="small">
                        {(options.fuel_types || []).filter(Boolean).map(f => (
                            <Option key={f} value={f}>{f}</Option>
                        ))}
                    </Select>
                </Col>
                <Col span={4}>
                    <div style={{ fontSize: 11, color: '#666', marginBottom: 3 }}>Status</div>
                    <Select value={filters.functional_status || undefined}
                        onChange={v => set('functional_status', v || '')}
                        placeholder="All Statuses" allowClear style={{ width: '100%' }} size="small">
                        {(options.statuses || []).filter(Boolean).map(s => (
                            <Option key={s} value={s}>{s}</Option>
                        ))}
                    </Select>
                </Col>
                <Col span={3}>
                    <div style={{ fontSize: 11, color: '#666', marginBottom: 3 }}>Ownership</div>
                    <Select value={filters.ownership || undefined} onChange={v => set('ownership', v || '')}
                        placeholder="All" allowClear style={{ width: '100%' }} size="small">
                        <Option value="purchased">Purchased</Option>
                        <Option value="donated">Donated</Option>
                    </Select>
                </Col>
                <Col span={5}>
                    <div style={{ fontSize: 11, color: '#666', marginBottom: 3 }}>Registration Plate</div>
                    <Input value={filters.registration} size="small"
                        onChange={e => set('registration', e.target.value)}
                        placeholder="Search plate..." allowClear />
                </Col>

                <Col span={6}>
                    <div style={{ fontSize: 11, color: '#666', marginBottom: 3 }}>
                        Odometer Range (km): {fmt(odomRange[0])} – {fmt(odomRange[1])}
                    </div>
                    <Slider range min={0} max={500000} step={5000}
                        value={odomRange}
                        onChange={v => setOdomRange(v)}
                        onAfterChange={v => onChange({ ...filters, odometer_min: v[0], odometer_max: v[1] })}
                    />
                </Col>
                <Col span={6}>
                    <div style={{ fontSize: 11, color: '#666', marginBottom: 3 }}>
                        Procurement Year: {filters.year_from || minYear} – {filters.year_to || maxYear}
                    </div>
                    <Slider range min={minYear} max={maxYear} step={1}
                        value={[filters.year_from || minYear, filters.year_to || maxYear]}
                        onChange={v => onChange({ ...filters, year_from: v[0], year_to: v[1] })}
                    />
                </Col>
                <Col span={12}>
                    <div style={{ fontSize: 11, color: '#666', marginBottom: 3 }}>
                        Compare Years (select 2–5 years for side-by-side comparison)
                    </div>
                    <Select mode="multiple" value={filters.compare_years ? filters.compare_years.split(',').map(Number).filter(Boolean) : []}
                        onChange={v => set('compare_years', v.join(','))}
                        placeholder="Select years to compare" style={{ width: '100%' }} size="small"
                        maxTagCount={5}>
                        {(options.years || []).map(y => (
                            <Option key={y} value={y}>{y}</Option>
                        ))}
                    </Select>
                </Col>
            </Row>
        </div>
    );
}

// ─── Comparative Chart ────────────────────────────────────────────────────────
function CompareChart({ data }) {
    if (!data || !Object.keys(data).length) return null;
    const rows = Object.values(data).map(d => ({
        year:       String(d.year),
        Total:      Number(d.total || 0),
        Functional: Number(d.functional || 0),
        Cost:       Number(d.cost || 0),
        Incidents:  Number(d.incidents || 0),
    }));

    return (
        <Row gutter={[12, 12]}>
            <Col span={12}>
                <ChartCard title="📊 Fleet Size & Operational — Year Comparison" height={260}>
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={rows} margin={{ top: 5, right: 20, left: 0, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                            <XAxis dataKey="year" tick={{ fontSize: 11 }} />
                            <YAxis tick={{ fontSize: 10 }} />
                            <RTooltip />
                            <Legend wrapperStyle={{ fontSize: 11 }} />
                            <Bar dataKey="Total" fill="#1890ff" name="Total Fleet" radius={[3,3,0,0]} />
                            <Bar dataKey="Functional" fill="#52c41a" name="Operational" radius={[3,3,0,0]} />
                        </BarChart>
                    </ResponsiveContainer>
                </ChartCard>
            </Col>
            <Col span={12}>
                <ChartCard title="💰 Maintenance Cost & Incidents — Year Comparison" height={260}>
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={rows} margin={{ top: 5, right: 20, left: 0, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                            <XAxis dataKey="year" tick={{ fontSize: 11 }} />
                            <YAxis yAxisId="left" tick={{ fontSize: 10 }} tickFormatter={v => `E${Number(v/1000).toFixed(0)}k`} />
                            <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10 }} />
                            <RTooltip formatter={(v, n) => n === 'Cost' ? [fmtE(v), n] : [fmt(v), n]} />
                            <Legend wrapperStyle={{ fontSize: 11 }} />
                            <Bar yAxisId="left" dataKey="Cost" fill="#faad14" name="Maint. Cost (E)" radius={[3,3,0,0]} />
                            <Bar yAxisId="right" dataKey="Incidents" fill="#f5222d" name="Incidents" radius={[3,3,0,0]} />
                        </BarChart>
                    </ResponsiveContainer>
                </ChartCard>
            </Col>
        </Row>
    );
}

// ─── Main Dashboard ───────────────────────────────────────────────────────────
const EMPTY_FILTERS = {
    region: '', facility_id: 0, fuel_type: '', functional_status: '',
    ownership: '', registration: '', odometer_min: 0, odometer_max: 0,
    year_from: 0, year_to: 0, compare_years: '',
};

export default function FleetDashboard() {
    const perms = fleetPerms();
    // Regional users are locked to their region — pre-fill and lock the filter
    const lockedRegion = perms.isRegional ? perms.region : '';

    const [data,       setData]       = useState(null);
    const [loading,    setLoading]    = useState(true);
    const [filters,    setFilters]    = useState({
        ...EMPTY_FILTERS,
        region: lockedRegion,  // pre-fill for regional users
    });
    const [options,    setOptions]    = useState({});
    const dashRef = useRef(null);

    // Prevent regional users from changing the region filter
    const isRegionalLocked   = perms.isRegional;
    // Prevent programme managers from changing the programme filter
    const isProgrammeLocked  = perms.isProgramme;

    const load = useCallback((f = filters) => {
        setLoading(true);
        const params = new URLSearchParams();
        Object.entries(f).forEach(([k, v]) => { if (v !== '' && v !== 0) params.append(k, v); });
        FleetService.getDashboard(params.toString())
            .then(res => {
                if (Number(res?.data?.code) === 200) {
                    setData(res.data.result);
                    if (res.data.result?.filter_options) {
                        setOptions(res.data.result.filter_options);
                    }
                }
            })
            .finally(() => setLoading(false));
    }, [filters]);

    useEffect(() => { load(EMPTY_FILTERS); }, []);

    const handleFilterChange = useCallback((newFilters) => {
        setFilters(newFilters);
        load(newFilters);
    }, [load]);

    const handleReset = () => {
        setFilters(EMPTY_FILTERS);
        load(EMPTY_FILTERS);
    };

    // Export single chart as PNG
    const exportChart = async (id, title) => {
        const el = document.getElementById(id);
        if (!el) return;
        const canvas = await html2canvas(el, { backgroundColor: '#ffffff', scale: 2 });
        const link = document.createElement('a');
        link.download = `${title.replace(/[^a-z0-9]/gi, '_')}.png`;
        link.href = canvas.toDataURL('image/png');
        link.click();
    };

    // Export all charts as PNG or Excel
    const exportAll = async (type = 'excel') => {
        if (type === 'png') {
            const el = dashRef.current;
            if (!el) return;
            const canvas = await html2canvas(el, { backgroundColor: '#ffffff', scale: 1.5 });
            const link = document.createElement('a');
            link.download = `Fleet_Dashboard_${new Date().toISOString().slice(0,10)}.png`;
            link.href = canvas.toDataURL('image/png');
            link.click();
            return;
        }
        // Excel export with all dashboard data
        if (!data) return;
        const wb = XLSX.utils.book_new();
        const addSheet = (name, rows) => {
            if (!rows?.length) return;
            XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), name);
        };
        addSheet('KPIs', [{
            'Total Fleet': data.total_fleet, 'Operational': data.functional_count,
            'Utilisation %': ((data.utilisation_rate||0)*100).toFixed(1)+'%',
            'Unassigned': data.unassigned, 'Open Incidents': data.open_incidents,
            'Expired Docs': data.expired_docs, 'Overdue Service': data.overdue_service,
        }]);
        addSheet('By Region',      data.by_region);
        addSheet('By Programme',   data.by_programme);
        addSheet('By Facility',    data.by_facility);
        addSheet('By Status',      data.by_status);
        addSheet('By Vehicle Type', data.by_vehicle_type);
        addSheet('By Manufacturer', data.by_manufacturer);
        addSheet('Age Profile',    data.age_profile);
        addSheet('Procurement Trend', data.procurement_trend);
        addSheet('Fuel by Month',  data.fuel_by_month);
        addSheet('Maintenance Trend', data.maintenance_cost_trend);
        addSheet('Top Cost per KM', data.top_cost_per_km);
        if (data.compare_data && Object.keys(data.compare_data).length) {
            addSheet('Year Comparison', Object.values(data.compare_data));
        }
        XLSX.writeFile(wb, `Fleet_Dashboard_${new Date().toISOString().slice(0,10)}.xlsx`);
    };

    const fuelByMonth = useMemo(() => {
        const map = {};
        (data?.fuel_by_month || []).forEach(r => {
            if (!map[r.month]) map[r.month] = { month: r.month, litres: 0, km: 0 };
            map[r.month].litres += Number(r.total_litres || 0);
            map[r.month].km     += Number(r.total_km     || 0);
        });
        return Object.values(map).filter(r => r.litres > 0 || r.km > 0)
            .sort((a, b) => a.month.localeCompare(b.month));
    }, [data]);

    const boarded = (data?.by_status || [])
        .filter(s => /board|dispos|grounded/i.test(s.status))
        .reduce((a, s) => a + Number(s.cnt || 0), 0);

    const hasCompare = data?.compare_data && Object.keys(data.compare_data).length > 0;

    if (loading && !data) return (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 80 }}>
            <Spin size="large" tip="Loading fleet dashboard…" />
        </div>
    );

    return (
        <div className="page-content" ref={dashRef}>
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <div>
                    <h4 style={{ margin: 0, fontWeight: 700 }}>Fleet Dashboard</h4>
                    <div style={{ fontSize: 12, color: '#888' }}>Ministry of Health — Kingdom of Eswatini · Fleet Management Overview</div>
                </div>
                {loading && <Spin size="small" />}
            </div>

            {/* Filter Bar */}
            <FilterBar
                options={options}
                filters={filters}
                onChange={handleFilterChange}
                onReset={handleReset}
                onExportAll={exportAll}
                loading={loading}
                isRegionalLocked={isRegionalLocked}
            />

            {/* ── KPI Row 1 ── */}
            <Row gutter={[10, 10]} style={{ marginBottom: 8 }}>
                <Col span={4}><KpiCard title="Total Fleet"       value={fmt(data?.total_fleet)}        icon={<CarOutlined />}     color="#1a5b9a" /></Col>
                <Col span={4}><KpiCard title="Operational"       value={fmt(data?.functional_count)}   icon={<CarOutlined />}     color="#52c41a" sub="good / fair condition" /></Col>
                <Col span={4}><KpiCard title="Boarded / Retired" value={fmt(boarded)}                  icon={<DeleteOutlined />}  color="#f5222d" /></Col>
                <Col span={4}><KpiCard title="Unassigned"        value={fmt(data?.unassigned)}         icon={<UserOutlined />}    color="#faad14" /></Col>
                <Col span={4}><KpiCard title="Open Incidents"    value={fmt(data?.open_incidents)}     icon={<AlertOutlined />}   color="#eb2f96" /></Col>
                <Col span={4}><KpiCard title="Utilisation Rate"  value={`${((data?.utilisation_rate||0)*100).toFixed(1)}%`} icon={<CarOutlined />} color="#722ed1" /></Col>
            </Row>
            <Row gutter={[10, 10]} style={{ marginBottom: 4 }}>
                <Col span={4}><KpiCard title="Expired Docs"     value={fmt(data?.expired_docs)}       icon={<WarningOutlined />} color="#f5222d" /></Col>
                <Col span={4}><KpiCard title="Expiring 30d"     value={fmt(data?.expiring_docs_30)}   icon={<WarningOutlined />} color="#faad14" /></Col>
                <Col span={4}><KpiCard title="Overdue Service"  value={fmt(data?.overdue_service)}    icon={<ToolOutlined />}    color="#d4380d" /></Col>
                <Col span={4}><KpiCard title="Service Due 30d"  value={fmt(data?.service_due_30)}     icon={<ToolOutlined />}    color="#d46b08" /></Col>
                <Col span={4}><KpiCard title="Pending Disposals" value={fmt(data?.pending_disposals)} icon={<DeleteOutlined />}  color="#595959" /></Col>
                <Col span={4}><KpiCard title="Ageing Fleet 15+"  value={fmt(data?.ageing_fleet)}      icon={<CarOutlined />}     color="#aaa" /></Col>
            </Row>
            {/* ── Odometer discrepancy alert row ── */}
            {(data?.odometer_discrepancies > 0) && (
                <div style={{ background:'#fff9f0', border:'1px solid #ffd591', borderRadius:8,
                    padding:'10px 16px', marginBottom:8, display:'flex', alignItems:'center', gap:16 }}>
                    <WarningOutlined style={{ color:'#fa8c16', fontSize:18 }}/>
                    <div>
                        <span style={{ fontWeight:600, color:'#d46b08' }}>
                            {fmt(data?.odometer_discrepancies)} odometer discrepanc{data?.odometer_discrepancies === 1 ? 'y' : 'ies'} detected
                        </span>
                        <span style={{ fontSize:12, color:'#888', marginLeft:12 }}>
                            Manual readings deviated &gt;20% from GPS map estimate — check Trip Logs for details
                        </span>
                    </div>
                    <span style={{ marginLeft:'auto', fontSize:12, color:'#888' }}>
                        {fmt(data?.odometer_high_discrepancies)} critical (&gt;50% deviation)
                    </span>
                </div>
            )}

            {/* ── Year Comparison ── */}
            {hasCompare && (
                <>
                    <SectionTitle color="#722ed1">📅 Year-on-Year Comparison</SectionTitle>
                    <CompareChart data={data.compare_data} />
                </>
            )}

            {/* ── Fleet Distribution ── */}
            <SectionTitle color="#1a5b9a">📊 Fleet Distribution</SectionTitle>
            <Row gutter={[12, 12]}>
                <Col span={8}>
                    <ChartCard title="🗺 Vehicles by Region" id="chart-region" onExport={exportChart} height={250}>
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie data={normPie(data?.by_region)} dataKey="cnt" nameKey="region"
                                    cx="50%" cy="50%" outerRadius={90} labelLine={false} label={PieLabel}>
                                    {normPie(data?.by_region).map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                                </Pie>
                                <RTooltip formatter={(v, n) => [fmt(v), n]} />
                                <Legend iconSize={10} wrapperStyle={{ fontSize: 10 }} />
                            </PieChart>
                        </ResponsiveContainer>
                    </ChartCard>
                </Col>
                <Col span={8}>
                    <ChartCard title="📋 Vehicles by Programme" id="chart-programme" onExport={exportChart} height={250}>
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie data={normPie(data?.by_programme)} dataKey="cnt" nameKey="programme"
                                    cx="50%" cy="50%" outerRadius={90} labelLine={false} label={PieLabel}>
                                    {normPie(data?.by_programme).map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                                </Pie>
                                <RTooltip formatter={(v, n) => [fmt(v), n]} />
                                <Legend iconSize={10} wrapperStyle={{ fontSize: 10 }} />
                            </PieChart>
                        </ResponsiveContainer>
                    </ChartCard>
                </Col>
                <Col span={8}>
                    <ChartCard title="⭐ Condition Rating (1–5)" id="chart-condition" onExport={exportChart} height={250}>
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie data={normPie(data?.by_condition)} dataKey="cnt" nameKey="condition_label"
                                    cx="50%" cy="50%" outerRadius={90} labelLine={false} label={PieLabel}>
                                    {normPie(data?.by_condition).map((entry, i) => {
                                        const r = Number(entry.rating || 0);
                                        const c = r === 5 ? '#52c41a'
                                                : r === 4 ? '#73d13d'
                                                : r === 3 ? '#faad14'
                                                : r === 2 ? '#fa8c16'
                                                : r === 1 ? '#f5222d'
                                                : '#d9d9d9';
                                        return <Cell key={i} fill={c} />;
                                    })}
                                </Pie>
                                <RTooltip formatter={(v, n) => [fmt(v), n]} />
                                <Legend iconSize={10} wrapperStyle={{ fontSize: 10 }} />
                            </PieChart>
                        </ResponsiveContainer>
                    </ChartCard>
                </Col>
            </Row>

            {/* Facility bar */}
            <Row gutter={[12, 12]} style={{ marginTop: 12 }}>
                <Col span={24}>
                    <ChartCard title="🏥 Vehicles per Facility" id="chart-facility" onExport={exportChart} height={280}>
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={toNum(data?.by_facility)} margin={{ top: 5, right: 10, left: 0, bottom: 80 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                <XAxis dataKey="facility" tick={{ fontSize: 9 }} angle={-40} textAnchor="end" interval={0} />
                                <YAxis tick={{ fontSize: 10 }} />
                                <RTooltip formatter={(v) => [fmt(v), 'Vehicles']} />
                                <Bar dataKey="cnt" fill="#1890ff" name="Vehicles" radius={[3,3,0,0]}>
                                    <LabelList dataKey="cnt" position="top" style={{ fontSize: 8, fill: '#555' }} />
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </ChartCard>
                </Col>
            </Row>

            {/* ── Vehicle Type & Age ── */}
            <SectionTitle color="#389e0d">🚗 Vehicle Composition & Age</SectionTitle>
            <Row gutter={[12, 12]}>
                <Col span={14}>
                    <ChartCard title="🚐 Top Vehicle Types" id="chart-types" onExport={exportChart} height={260}>
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={toNum((data?.by_vehicle_type || []).slice(0, 12))}
                                layout="vertical" margin={{ top: 0, right: 40, left: 100, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                                <XAxis type="number" tick={{ fontSize: 10 }} />
                                <YAxis type="category" dataKey="vehicle_type" tick={{ fontSize: 10 }} width={95} />
                                <RTooltip formatter={(v) => [fmt(v), 'Vehicles']} />
                                <Bar dataKey="cnt" fill="#1890ff" name="Total" radius={[0,3,3,0]}>
                                    <LabelList dataKey="cnt" position="right" style={{ fontSize: 9, fill: '#555' }} />
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </ChartCard>
                </Col>
                <Col span={10}>
                    <ChartCard title="📅 Age Profile" id="chart-age" onExport={exportChart} height={260}>
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={toNum(data?.age_profile)} margin={{ top: 5, right: 20, left: 0, bottom: 50 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                <XAxis dataKey="age_bracket" tick={{ fontSize: 8 }} angle={-30} textAnchor="end" interval={0} />
                                <YAxis tick={{ fontSize: 10 }} />
                                <RTooltip />
                                <Bar dataKey="cnt" name="Vehicles" radius={[3,3,0,0]}>
                                    {toNum(data?.age_profile).map((entry, i) => {
                                        const c = entry.age_bracket?.startsWith('0-2') ? '#52c41a'
                                                : entry.age_bracket?.startsWith('3-5') ? '#73d13d'
                                                : entry.age_bracket?.startsWith('6-10') ? '#faad14'
                                                : entry.age_bracket?.startsWith('11-15') ? '#fa8c16'
                                                : '#f5222d';
                                        return <Cell key={i} fill={c} />;
                                    })}
                                    <LabelList dataKey="cnt" position="top" style={{ fontSize: 9, fill: '#555' }} />
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </ChartCard>
                </Col>
            </Row>

            {/* ── Costs & Fuel ── */}
            <SectionTitle color="#d46b08">💰 Costs & Fuel Consumption</SectionTitle>
            <Row gutter={[12, 12]}>
                <Col span={24}>
                    <ChartCard title="🏆 Top 20 Vehicles — Cost per KM (E/km)" id="chart-cpkm" onExport={exportChart} height={280}>
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={toNum(data?.top_cost_per_km, 'cost_per_km')}
                                margin={{ top: 5, right: 20, left: 10, bottom: 70 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                <XAxis dataKey="registration_no" tick={{ fontSize: 9 }} angle={-40} textAnchor="end" interval={0} />
                                <YAxis tick={{ fontSize: 10 }} tickFormatter={v => `E${v}`} />
                                <RTooltip formatter={(v) => [`E ${Number(v).toFixed(2)}/km`, 'Cost/km']} />
                                <Bar dataKey="cost_per_km" fill="#f5222d" name="Cost per KM (E)" radius={[3,3,0,0]}>
                                    <LabelList dataKey="cost_per_km" position="top"
                                        formatter={v => `E${Number(v).toFixed(1)}`}
                                        style={{ fontSize: 8, fill: '#555' }} />
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </ChartCard>
                </Col>
            </Row>
            <Row gutter={[12, 12]} style={{ marginTop: 12 }}>
                <Col span={14}>
                    <ChartCard title="⛽ Fuel Consumption by Month" id="chart-fuel" onExport={exportChart} height={260}>
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={fuelByMonth} margin={{ top: 5, right: 20, left: 0, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                                <YAxis tick={{ fontSize: 10 }} />
                                <RTooltip formatter={(v, n) => [n === 'litres' ? `${fmt(v)} L` : `${fmt(v)} km`, n === 'litres' ? 'Litres' : 'KM']} />
                                <Legend wrapperStyle={{ fontSize: 11 }} />
                                <Bar dataKey="litres" fill="#52c41a" name="Litres" radius={[3,3,0,0]} />
                                <Bar dataKey="km" fill="#1890ff" name="KM Driven" radius={[3,3,0,0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </ChartCard>
                </Col>
                <Col span={10}>
                    <ChartCard title="🔧 Maintenance Cost Trend (12 months)" id="chart-maint" onExport={exportChart} height={260}>
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={toNum(data?.maintenance_cost_trend, 'total_cost')}
                                margin={{ top: 5, right: 20, left: 0, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                                <YAxis tick={{ fontSize: 10 }} tickFormatter={v => `E${Number(v/1000).toFixed(0)}k`} />
                                <RTooltip formatter={(v) => [fmtE(v), 'Maintenance Cost']} />
                                <Line type="monotone" dataKey="total_cost" stroke="#faad14"
                                    strokeWidth={2} dot={{ r: 3 }} name="Cost (E)" />
                            </LineChart>
                        </ResponsiveContainer>
                    </ChartCard>
                </Col>
            </Row>

            {/* ── Deployment Dashboard ── */}
            {data?.deployment_summary && (data.deployment_summary.active > 0 || data.deployment_summary.overdue > 0 || data.deployment_summary.returned > 0) && (
                <>
                    <SectionTitle color="#d46b08">🚩 Active Deployments</SectionTitle>
                    <Row gutter={[12, 12]}>
                        <Col span={6}>
                            <Row gutter={[12, 12]}>
                                {[
                                    { label:'Vehicles Out',    value: data.deployment_summary.vehicles_out,  color:'#1890ff' },
                                    { label:'Active',          value: data.deployment_summary.active,         color:'#d46b08' },
                                    { label:'Overdue Return',  value: data.deployment_summary.overdue,        color:'#f5222d' },
                                    { label:'International',   value: data.deployment_summary.international,  color:'#722ed1' },
                                ].map(({ label, value, color }) => (
                                    <Col span={24} key={label}>
                                        <div style={{ background:'#fff', border:'1px solid #e8e8e8',
                                            borderRadius:8, padding:'10px 14px', borderLeft:`3px solid ${color}` }}>
                                            <div style={{ fontSize:11, color:'#888' }}>{label}</div>
                                            <div style={{ fontSize:22, fontWeight:700, color }}>{fmt(value)}</div>
                                        </div>
                                    </Col>
                                ))}
                            </Row>
                        </Col>
                        <Col span={8}>
                            <ChartCard title="🚩 Active Deployments by Type" id="chart-deploy-type" onExport={exportChart} height={240}>
                                <ResponsiveContainer width="100%" height="100%">
                                    <PieChart>
                                        <Pie data={normPie(data.deployment_summary.by_type, 'cnt')}
                                            dataKey="cnt" nameKey="type"
                                            cx="50%" cy="50%" outerRadius={85}
                                            labelLine={false} label={PieLabel}>
                                            {normPie(data.deployment_summary.by_type, 'cnt').map((_, i) => (
                                                <Cell key={i} fill={['#1890ff','#d46b08','#722ed1'][i % 3]} />
                                            ))}
                                        </Pie>
                                        <RTooltip formatter={(v, n) => [fmt(v), n]} />
                                        <Legend iconSize={10} wrapperStyle={{ fontSize:10 }} />
                                    </PieChart>
                                </ResponsiveContainer>
                            </ChartCard>
                        </Col>
                        <Col span={10}>
                            <ChartCard title="📋 Recent Deployments" id="chart-deploy-recent" onExport={exportChart} height={240}>
                                <div style={{ overflowY:'auto', height:'100%' }}>
                                    {(data.deployment_summary.recent || []).map((d, i) => (
                                        <div key={i} style={{ display:'flex', justifyContent:'space-between',
                                            alignItems:'center', padding:'6px 4px',
                                            borderBottom:'1px solid #f5f5f5', fontSize:11 }}>
                                            <div>
                                                <div style={{ fontWeight:600, color:'#333' }}>{d.event_name}</div>
                                                <div style={{ color:'#aaa', fontSize:10 }}>
                                                    {d.deployment_type} · {d.vehicle_count} vehicle{d.vehicle_count != 1 ? 's' : ''}
                                                    {d.is_international == 1 ? ' · 🌍' : ''}
                                                </div>
                                            </div>
                                            <span style={{ fontSize:10, fontWeight:600, padding:'2px 6px',
                                                borderRadius:8,
                                                background: d.status === 'Overdue' ? '#fff1f0' : d.status === 'Active' ? '#e6f4ff' : '#f6ffed',
                                                color: d.status === 'Overdue' ? '#cf1322' : d.status === 'Active' ? '#1890ff' : '#389e0d',
                                                border: `1px solid ${d.status === 'Overdue' ? '#ffa39e' : d.status === 'Active' ? '#91d5ff' : '#b7eb8f'}`,
                                            }}>{d.status}</span>
                                        </div>
                                    ))}
                                    {(!data.deployment_summary.recent || !data.deployment_summary.recent.length) && (
                                        <div style={{ textAlign:'center', color:'#aaa', padding:20, fontSize:12 }}>
                                            No active deployments
                                        </div>
                                    )}
                                </div>
                            </ChartCard>
                        </Col>
                    </Row>
                </>
            )}

            {/* ── Incident Dashboard ── */}
            <SectionTitle color="#cf1322">🚨 Incident Dashboard</SectionTitle>
            <Row gutter={[12, 12]}>
                {/* Status pie */}
                <Col span={7}>
                    <ChartCard title="🚨 Incidents by Status" id="chart-inc-status" onExport={exportChart} height={240}>
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie data={normPie(data?.by_incident_status)} dataKey="cnt" nameKey="status"
                                    cx="50%" cy="50%" outerRadius={85} labelLine={false} label={PieLabel}>
                                    {normPie(data?.by_incident_status).map((entry, i) => {
                                        const c = /open/i.test(entry.status) ? '#f5222d'
                                                : /resolv/i.test(entry.status) ? '#52c41a'
                                                : '#faad14';
                                        return <Cell key={i} fill={c} />;
                                    })}
                                </Pie>
                                <RTooltip formatter={(v, n) => [fmt(v), n]} />
                                <Legend iconSize={10} wrapperStyle={{ fontSize:10 }} />
                            </PieChart>
                        </ResponsiveContainer>
                    </ChartCard>
                </Col>

                {/* Type bar */}
                <Col span={10}>
                    <ChartCard title="📋 Incidents by Type" id="chart-inc-type" onExport={exportChart} height={240}>
                        {(data?.by_incident_type || []).length === 0 ? (
                            <div style={{ display:'flex', flexDirection:'column', justifyContent:'center',
                                alignItems:'center', height:'100%', color:'#aaa' }}>
                                <div style={{ fontSize:32, fontWeight:700, color:'#f5222d' }}>{fmt(data?.open_incidents)}</div>
                                <div style={{ fontSize:12 }}>Open Incidents</div>
                                <div style={{ fontSize:11, marginTop:8 }}>No incidents recorded yet</div>
                            </div>
                        ) : (
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={toNum(data?.by_incident_type, 'cnt').sort((a,b)=>b.cnt-a.cnt)}
                                    layout="vertical" margin={{ top:0, right:40, left:110, bottom:0 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                                    <XAxis type="number" tick={{ fontSize:10 }} />
                                    <YAxis type="category" dataKey="type" tick={{ fontSize:10 }} width={105} />
                                    <RTooltip formatter={(v, n) => [fmt(v), n === 'cnt' ? 'Total' : 'Open']} />
                                    <Legend wrapperStyle={{ fontSize:10 }} />
                                    <Bar dataKey="cnt" fill="#faad14" name="Total" radius={[0,3,3,0]}>
                                        <LabelList dataKey="cnt" position="right" style={{ fontSize:9, fill:'#555' }} />
                                    </Bar>
                                    <Bar dataKey="open_cnt" fill="#f5222d" name="Open" radius={[0,3,3,0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        )}
                    </ChartCard>
                </Col>

                {/* Summary cards */}
                <Col span={7}>
                    <ChartCard title="📦 Fleet Alerts" id="chart-alerts" onExport={exportChart} height={240}>
                        <div style={{ display:'flex', flexDirection:'column', gap:10, padding:'8px 0' }}>
                            {[
                                { label:'Open Incidents',     value: data?.open_incidents || 0,    color:'#f5222d', link:'fleet-incidents' },
                                { label:'Expired Documents',  value: data?.expired_docs || 0,       color:'#cf1322', link:null },
                                { label:'Expiring (30d)',     value: data?.expiring_docs_30 || 0,   color:'#faad14', link:null },
                                { label:'Overdue Service',    value: data?.overdue_service || 0,    color:'#d4380d', link:'fleet-maintenance' },
                                { label:'Pending Disposals',  value: data?.pending_disposals || 0,  color:'#595959', link:'fleet-disposals' },
                            ].map(({ label, value, color, link }) => (
                                <div key={label} style={{ display:'flex', justifyContent:'space-between',
                                    alignItems:'center', padding:'6px 10px', borderRadius:6,
                                    background: Number(value) > 0 ? '#fff8f8' : '#f9f9f9',
                                    border:`1px solid ${Number(value) > 0 ? '#ffa39e' : '#e8e8e8'}` }}>
                                    <span style={{ fontSize:12, color:'#595959' }}>{label}</span>
                                    <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                                        <span style={{ fontSize:18, fontWeight:700, color:Number(value)>0?color:'#aaa' }}>
                                            {fmt(value)}
                                        </span>
                                        {link && Number(value) > 0 && (
                                            <span style={{ fontSize:10, color:'#1677ff', cursor:'pointer' }}
                                                onClick={() => window.location.hash=`#/${link}`}>→</span>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </ChartCard>
                </Col>
            </Row>

            {/* ── Composition ── */}
            <SectionTitle color="#722ed1">🏭 Fleet Composition</SectionTitle>
            <Row gutter={[12, 12]}>
                <Col span={8}>
                    <ChartCard title="🏭 By Manufacturer" id="chart-make" onExport={exportChart} height={260}>
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie data={normPie(data?.by_manufacturer, 'total')} dataKey="total" nameKey="manufacturer"
                                    cx="50%" cy="50%" outerRadius={90} labelLine={false} label={PieLabel}>
                                    {normPie(data?.by_manufacturer, 'total').map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                                </Pie>
                                <RTooltip formatter={(v, n) => [fmt(v), n]} />
                                <Legend iconSize={10} wrapperStyle={{ fontSize: 10 }} />
                            </PieChart>
                        </ResponsiveContainer>
                    </ChartCard>
                </Col>
                <Col span={8}>
                    <ChartCard title="🏷 Registration Type" id="chart-regtype" onExport={exportChart} height={260}>
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie data={normPie(data?.registration_type)} dataKey="cnt" nameKey="reg_type"
                                    cx="50%" cy="50%" outerRadius={90} labelLine={false} label={PieLabel}>
                                    {normPie(data?.registration_type).map((_, i) => (
                                        <Cell key={i} fill={['#1890ff','#52c41a','#faad14'][i % 3]} />
                                    ))}
                                </Pie>
                                <RTooltip formatter={(v, n) => [fmt(v), n]} />
                                <Legend iconSize={10} wrapperStyle={{ fontSize: 10 }} />
                            </PieChart>
                        </ResponsiveContainer>
                    </ChartCard>
                </Col>
                <Col span={8}>
                    <ChartCard title="⚡ Fleet Status Breakdown" id="chart-status" onExport={exportChart} height={260}>
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={toNum(data?.by_status).sort((a,b) => b.cnt - a.cnt)}
                                layout="vertical" margin={{ top: 0, right: 50, left: 110, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                                <XAxis type="number" tick={{ fontSize: 10 }} />
                                <YAxis type="category" dataKey="status" tick={{ fontSize: 10 }} width={105} />
                                <RTooltip formatter={(v) => [fmt(v), 'Vehicles']} />
                                <Bar dataKey="cnt" name="Vehicles" radius={[0,3,3,0]}>
                                    {toNum(data?.by_status).sort((a,b) => b.cnt - a.cnt).map((entry, i) => {
                                        const s = (entry.status || '').toLowerCase();
                                        const c = /board|dispos|ground/i.test(s) ? '#f5222d'
                                                : /repair|await/i.test(s) ? '#faad14'
                                                : /good|function|fair/i.test(s) ? '#52c41a'
                                                : '#1890ff';
                                        return <Cell key={i} fill={c} />;
                                    })}
                                    <LabelList dataKey="cnt" position="right" style={{ fontSize: 9, fill: '#555' }} />
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </ChartCard>
                </Col>
            </Row>
            <Row gutter={[12, 12]} style={{ marginTop: 12 }}>
                <Col span={24}>
                    <ChartCard title="📈 Annual Procurement Trend" id="chart-trend" onExport={exportChart} height={240}>
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={toNum(data?.procurement_trend, 'cnt')}
                                margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                <XAxis dataKey="yr" tick={{ fontSize: 10 }} />
                                <YAxis tick={{ fontSize: 10 }} />
                                <RTooltip formatter={(v) => [fmt(v), 'Registered']} />
                                <Bar dataKey="cnt" fill="#722ed1" name="Registered" radius={[3,3,0,0]}>
                                    <LabelList dataKey="cnt" position="top" style={{ fontSize: 8, fill: '#555' }} />
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </ChartCard>
                </Col>
            </Row>
            <div style={{ height: 40 }} />
        </div>
    );
}